/**
 * RMA processing form — shipping card: shipping contact, the
 * "included on another RMA" toggle, and tracking-number / shipping-cost
 * entry (or the linked-RMA selector when the cost lives elsewhere).
 *
 * Hidden entirely for quick-ship RMAs.
 *
 * Contains:
 * - ShippingSection (default export)
 */

import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { Field } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ContactSelector } from '@sophocles/components/contacts';

import { SectionCard } from '../../components/ReturnSection';
import RecentTrackingRmaSelect from '../fields/RecentTrackingRmaSelect';
import { isValidShippingCost } from '../validation';

export default function ShippingSection({
  isQuickShip,
  selectedShippingContact,
  onShippingContactChange,
  customerId,
  siteGroupId,
  customer,
  siteGroup,
  shippingIncludedCheckbox,
  onToggleShippingIncluded,
  shippingCostError,
  onShippingCostError,
  shippingCostIncludedOnRmaId,
  onShippingCostIncludedChange,
  shippingCostIncludedOnRma,
}) {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('rma.shipping', 'Shipping')}>
      {!isQuickShip && (
        <Box sx={{ mb: 2 }}>
          <ContactSelector
            value={selectedShippingContact}
            onChange={onShippingContactChange}
            customerId={customerId}
            siteGroupId={siteGroupId}
            customer={customer}
            siteGroup={siteGroup}
            label={t('rma.shippingContact', 'Shipping Contact')}
            addressType="shipping"
            required={true}
            requiredFields={['address', 'name']}
            primaryContext="rma"
            contextLabel={t('rma.rma', 'RMA')}
          />
        </Box>
      )}

      {!isQuickShip && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('rma.controlNumber', 'Control Number')}{' '}
            <Box component="span" sx={{ fontWeight: 'normal', fontSize: '12px' }}>
              {t('rma.comingSoon', '(coming soon)')}
            </Box>
          </Typography>
        </Box>
      )}

      {!isQuickShip && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            label={t('rma.trackingShippingIncludedOther', 'Tracking # and shipping cost included on other RMA')}
            control={<Checkbox checked={shippingIncludedCheckbox} onChange={onToggleShippingIncluded} />}
          />
        </Box>
      )}

      {!isQuickShip &&
        (!shippingIncludedCheckbox ? (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('rma.trackingNumber', 'Tracking Number')}
              </Typography>
              <Field name="trackingNumber">
                {({ field }) => (
                  <TextField {...field} size="small" inputProps={{ maxLength: 50 }} sx={{ width: 256 }} />
                )}
              </Field>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('rma.shippingCost', 'Shipping Cost')}
              </Typography>
              <Field name="shippingCost">
                {({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    onChange={e => {
                      field.onChange(e);
                      const val = e.target.value;
                      if (!isValidShippingCost(val)) {
                        onShippingCostError(t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'));
                      } else {
                        onShippingCostError(null);
                      }
                    }}
                    error={!!shippingCostError}
                    helperText={shippingCostError}
                    inputProps={{ maxLength: 10 }}
                    sx={{ width: 256 }}
                  />
                )}
              </Field>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ mb: 2 }}>
            <RecentTrackingRmaSelect
              siteGroupId={siteGroupId}
              value={shippingCostIncludedOnRmaId}
              onChange={onShippingCostIncludedChange}
              selectedRma={shippingCostIncludedOnRma}
            />
          </Box>
        ))}
    </SectionCard>
  );
}
